/**
 * HTML transforms used by the Communication editor + email templates.
 *
 * Pure functions only — no React, no DOM mutation of mounted nodes. Each
 * helper takes a raw HTML string and returns a new HTML string with the
 * requested change applied. Safe to run repeatedly (idempotent).
 */

/**
 * Force `text-decoration: none` (and an inherited color so links blend into
 * the surrounding text) on every `<a>` tag in the supplied HTML string.
 *
 * Uses the browser's DOMParser to avoid the usual regex pitfalls with
 * nested quotes, attribute order, and `<a>` tags that span lines. We
 * preserve any existing inline styles the admin already set inside the
 * Unlayer designer or the rich-text editor — the new declarations are
 * appended so they take precedence in the cascade.
 */
export function stripLinkUnderline(html) {
  if (!html || typeof html !== "string") return html;
  // DOMParser only handles full documents reliably — wrap into a doc, then
  // unwrap. We touch every <a> regardless of where it lives.
  let doc;
  try {
    doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  } catch {
    return html;
  }
  const anchors = doc.querySelectorAll("a");
  if (anchors.length === 0) return html;
  anchors.forEach((a) => {
    const existing = (a.getAttribute("style") || "").trim();
    // If the style already explicitly opts out of underlining we can leave
    // it alone — keeps the function idempotent across multiple saves.
    if (/text-decoration\s*:\s*none/i.test(existing)) return;
    const cleaned = existing
      // Drop any stale text-decoration declarations so our `none` wins.
      .replace(/text-decoration\s*:[^;]*;?/gi, "")
      .trim();
    const next = [
      cleaned,
      "text-decoration: none",
    ]
      .filter(Boolean)
      .join("; ")
      .replace(/;\s*;/g, ";")
      .replace(/^;\s*/, "");
    a.setAttribute("style", next.endsWith(";") ? next : `${next};`);
  });
  // Re-serialise just the body contents (no <html><head> wrapper)
  return doc.body.innerHTML;
}
